/**
 * Standalone "Team Chat" page at /notes.
 *
 * The same NotesInbox that reps see as a tab inside their workspace,
 * given its own route so it isn't locked behind the `field_rep` role.
 * Notes can be addressed to ANY active user — a depletions analyst or
 * a non-rep admin can receive one — so gating the only entry point on
 * the rep role would leave those people with mail they can't open.
 *
 * Authenticated-only (no role gate): see the route in App.tsx.
 *
 * Page chrome matches the Regional Sales / Admin pattern — gold
 * eyebrow, ink H1, italic dek — so the section reads as part of the
 * same product rather than a bolted-on inbox.
 */

import { Box, Container, Typography } from '@mui/material';

import { NotesInbox } from '../components/NotesInbox';
import { SiteLayout } from '../components/SiteLayout';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function NotesPage() {
  // `fitViewport`: this page scrolls internally (the transcript and the
  // conversation list each have their own scroll), so the shell is
  // pinned to the viewport. That gives the flex chain a definite height
  // to divide, which is what keeps the footer on screen — with the
  // default `minHeight`, a tall transcript just grows the page and
  // pushes the footer below the fold.
  return (
    <SiteLayout subtitle="Team Chat" fitViewport>
      {/* `lg` rather than `md`: the surface is two panes, so capping at
          900px left roughly 600px for the transcript on any screen —
          narrower than the list pane deserves. The heading block keeps
          its own measure below.

          Flex column with `minHeight: 0` so the chat panel can take
          exactly the space the shell leaves after the header, this
          heading, and the footer. SiteLayout already gives its child
          `flex: 1`; carrying that down means neither the header nor
          the footer can be pushed off-screen by a tall transcript. */}
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 2.5, md: 5 },
          pb: { xs: 3, md: 6 },
          px: { xs: 1.5, sm: 3 },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ mb: { xs: 2.5, md: 3 }, flexShrink: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: GOLD,
              mb: 0.75,
            }}
          >
            Team Chat
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT_INTER,
              fontWeight: 700,
              fontSize: { xs: 26, md: 34 },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: INK,
            }}
          >
            Messages &amp; Hand-offs
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              maxWidth: 640,
              fontFamily: FONT_INTER,
              fontStyle: 'italic',
              fontSize: { xs: 13.5, md: 14.5 },
              color: 'rgba(15, 10, 7, 0.62)',
              lineHeight: 1.55,
            }}
          >
            Direct and group messages between you and the rest of the team — asks, hand-offs, and
            anything worth putting in writing.
          </Typography>
        </Box>

        <NotesInbox fillParent />
      </Container>
    </SiteLayout>
  );
}
